import { useEffect, useState, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchStoreCatalog,
  fetchStoreInfo,
} from "../../../lib/webService/webService";
import PropTypes from "prop-types";
import { PullToRefreshWrapper } from "../../../components/pullRefresh";
import { BoxSeam, PlusLg, ArrowClockwise } from "react-bootstrap-icons";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("pr-styles")) {
  const s = document.createElement("style");
  s.id = "pr-styles";
  s.textContent = `
    @keyframes _pr_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._pr_sk { animation: _pr_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .pr-card { transition: transform 0.22s cubic-bezier(0.34,1.24,0.64,1), box-shadow 0.22s ease; cursor: pointer; }
    .pr-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
    .pr-card:active { transform: scale(.985); }
  `;
  document.head.appendChild(s);
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_pr_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const ProductCard = ({
  loading,
  id,
  name,
  description,
  price,
  category,
  storeId,
}) => {
  const navigate = useNavigate();

  const navigateToProduct = useCallback(() => {
    if (!loading && storeId && id) {
      navigate(`/sup/stores/${storeId}/${id}`);
    }
  }, [loading, storeId, id, navigate]);

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 16,
          border: "1px solid rgba(60,60,67,0.08)",
          padding: "14px 16px",
        }}
      >
        <Skel w={58} h={11} r={5} style={{ marginBottom: 10 }} />
        <Skel w="78%" h={18} r={6} style={{ marginBottom: 8 }} />
        <Skel w={74} h={22} r={11} style={{ marginBottom: 10 }} />
        <Skel w="90%" h={12} r={5} style={{ marginBottom: 5 }} />
        <Skel w="65%" h={12} r={5} style={{ marginBottom: 16 }} />
        <Skel w={66} h={20} r={6} />
      </div>
    );
  }

  return (
    <div
      className="pr-card"
      onClick={navigateToProduct}
      style={{
        backgroundColor: "#fff",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.09)",
        padding: "14px 16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* ID */}
      <span
        style={{
          fontSize: "0.7rem",
          fontWeight: 500,
          color: "rgba(60,60,67,0.33)",
          marginBottom: 7,
          display: "block",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        #{id}
      </span>

      {/* Name */}
      <h3
        style={{
          fontSize: "0.9375rem",
          fontWeight: 600,
          color: "#1c1c1e",
          margin: "0 0 8px",
          lineHeight: 1.3,
          letterSpacing: "-0.01em",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {name}
      </h3>

      {/* Category */}
      {category && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 10px",
            borderRadius: 20,
            backgroundColor: "rgba(60,60,67,0.07)",
            color: "rgba(60,60,67,0.58)",
            fontSize: "0.72rem",
            fontWeight: 500,
            marginBottom: 10,
            alignSelf: "flex-start",
            letterSpacing: "0.01em",
          }}
        >
          {category}
        </span>
      )}

      {/* Description */}
      {description && (
        <p
          style={{
            fontSize: "0.8125rem",
            color: "rgba(60,60,67,0.55)",
            lineHeight: 1.45,
            margin: "0 0 12px",
            flex: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </p>
      )}

      {/* Price */}
      {price > 0 && (
        <div
          style={{
            marginTop: "auto",
            paddingTop: 10,
            borderTop: "1px solid rgba(60,60,67,0.07)",
          }}
        >
          <span
            style={{
              fontSize: "0.9375rem",
              fontWeight: 700,
              color: "#1c1c1e",
              letterSpacing: "-0.02em",
            }}
          >
            KES {price.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
};

ProductCard.propTypes = {
  loading: PropTypes.bool,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  price: PropTypes.number,
  category: PropTypes.string,
  storeId: PropTypes.string.isRequired,
};

ProductCard.defaultProps = {
  loading: false,
  description: "",
  price: 0,
  category: "",
};

const StoreHeader = ({ loading, store, productsCount }) => {
  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 16,
          border: "1px solid rgba(60,60,67,0.08)",
          padding: "18px 20px",
          marginBottom: 20,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <Skel w={54} h={54} r={14} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Skel w="52%" h={20} r={6} style={{ marginBottom: 8 }} />
            <Skel w="36%" h={14} r={5} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Skel w={90} h={26} r={13} />
          <Skel w={102} h={26} r={13} />
        </div>
      </div>
    );
  }

  if (!store) return null;

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.09)",
        padding: "18px 20px",
        marginBottom: 20,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        {store.imgSrc && (
          <img
            src={store.imgSrc}
            alt={store.store_name}
            style={{ width: 54, height: 54, borderRadius: 14, objectFit: "cover", flexShrink: 0 }}
          />
        )}
        <div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", margin: "0 0 3px", letterSpacing: "-0.02em" }}>
            {store.store_name}
          </h3>
          <p style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.52)", margin: 0, fontWeight: 500 }}>
            {store.store_category}
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: 20,
            backgroundColor: "rgba(60,60,67,0.07)",
            color: "rgba(60,60,67,0.62)",
            fontSize: "0.78rem",
            fontWeight: 500,
          }}
        >
          {productsCount} Products
        </span>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: 20,
            backgroundColor: "rgba(60,60,67,0.07)",
            color: "rgba(60,60,67,0.62)",
            fontSize: "0.78rem",
            fontWeight: 500,
          }}
        >
          {store.rating ? `${store.rating.toFixed(1)} ★` : "No ratings"}
        </span>
      </div>
    </div>
  );
};

StoreHeader.propTypes = {
  loading: PropTypes.bool,
  store: PropTypes.shape({
    store_name: PropTypes.string,
    store_category: PropTypes.string,
    imgSrc: PropTypes.string,
    rating: PropTypes.number,
  }),
  productsCount: PropTypes.number,
};

StoreHeader.defaultProps = {
  loading: false,
  store: null,
  productsCount: 0,
};

const Products = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    products: [],
    loading: true,
    error: null,
    retryCount: 0,
    store: null,
    storeLoading: true,
  });

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  const fetchStoreDetails = useCallback(async () => {
    if (!storeId) return;

    try {
      const data = await fetchStoreInfo(storeId);
      setState((prev) => ({
        ...prev,
        store: data,
        storeLoading: false,
      }));
    } catch (error) {
      console.error("Error fetching store details:", error);
      setState((prev) => ({
        ...prev,
        storeLoading: false,
      }));
    }
  }, [storeId]);

  const fetchProducts = useCallback(async () => {
    if (!storeId) {
      setState((prev) => ({
        ...prev,
        error: "Store ID is required",
        loading: false,
      }));
      return;
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const data = await fetchStoreCatalog(storeId);

      if (!Array.isArray(data)) {
        throw new Error("Invalid data format received from server");
      }

      setState((prev) => ({
        ...prev,
        products: data,
        loading: false,
        error: null,
        retryCount: 0,
      }));
    } catch (error) {
      console.error("Error fetching products:", error);

      if (state.retryCount < MAX_RETRIES) {
        setState((prev) => ({
          ...prev,
          error: `Failed to load products. Retrying... (${
            prev.retryCount + 1
          }/${MAX_RETRIES})`,
          retryCount: prev.retryCount + 1,
        }));

        setTimeout(fetchProducts, RETRY_DELAY);
      } else {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load products. Please try again later.",
        }));
      }
    }
  }, [storeId, state.retryCount]);

  useEffect(() => {
    fetchStoreDetails();
    fetchProducts();
  }, [fetchStoreDetails, fetchProducts]);

  const navigateToAddProduct = useCallback(() => {
    if (storeId) {
      navigate(`/sup/stores/${storeId}/products/add`);
    }
  }, [storeId, navigate]);

  const handleRetry = useCallback(() => {
    setState((prev) => ({ ...prev, retryCount: 0 }));
    fetchProducts();
  }, [fetchProducts]);

  const refreshProductsData = useCallback(async () => {
    await Promise.all([fetchStoreDetails(), fetchProducts()]);
  }, [fetchStoreDetails, fetchProducts]);

  const renderContent = () => {
    if (state.loading) {
      return (
        <Row className="g-3">
          {Array(6)
            .fill(null)
            .map((_, index) => (
              <Col key={index} xs={12} sm={6} lg={4}>
                <ProductCard
                  loading={true}
                  storeId={storeId}
                  id={`skeleton-${index}`}
                  name=""
                />
              </Col>
            ))}
        </Row>
      );
    }

    if (state.error) {
      return (
        <div
          style={{
            backgroundColor: "rgba(255,59,48,0.08)",
            borderRadius: 12,
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ fontSize: "0.875rem", color: "#FF3B30" }}>{state.error}</span>
          {state.retryCount < MAX_RETRIES && (
            <button
              onClick={handleRetry}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                padding: 0,
                fontSize: "0.8125rem",
                color: "#FF3B30",
                fontWeight: 600,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <ArrowClockwise size={13} /> Retry
            </button>
          )}
        </div>
      );
    }

    if (state.products.length === 0) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "64px 24px",
            textAlign: "center",
            background: "#fff",
            border: "1px solid rgba(60,60,67,0.08)",
            borderRadius: 16,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              backgroundColor: "rgba(60,60,67,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <BoxSeam size={26} color="rgba(60,60,67,0.28)" />
          </div>
          <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>No products yet</h4>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.45)", margin: 0 }}>
            Click "Add product" to create your first product.
          </p>
        </div>
      );
    }

    return (
      <Row className="g-3">
        {state.products.map((item) => (
          <Col key={item.id} xs={12} sm={6} lg={4}>
            <ProductCard loading={false} storeId={storeId} {...item} />
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <PullToRefreshWrapper onRefresh={refreshProductsData}>
      <Seo {...generateNoIndexSEO("Store Products")} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "12px 4px 48px", fontFamily: FONT }}>
        <StoreHeader
          loading={state.storeLoading}
          store={state.store}
          productsCount={state.products?.length || 0}
        />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ fontSize: "1.0625rem", fontWeight: 600, color: "#1c1c1e", letterSpacing: "-0.02em", margin: 0 }}>
            Products
          </h3>
          <button
            onClick={navigateToAddProduct}
            disabled={!storeId}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 18px",
              borderRadius: 20,
              border: "none",
              backgroundColor: "#007AFF",
              color: "#fff",
              fontSize: "0.8125rem",
              fontWeight: 600,
              cursor: storeId ? "pointer" : "not-allowed",
              opacity: storeId ? 1 : 0.5,
              transition: "opacity 0.15s ease",
              fontFamily: FONT,
            }}
          >
            <PlusLg size={13} /> Add product
          </button>
        </div>

        {renderContent()}
      </div>
    </PullToRefreshWrapper>
  );
};

export default Products;
